import type { PricingConfigPayload, PricingConfigSummary } from '../../services/api';

export type AdminDashboardTab = 'companies' | 'sows' | 'users' | 'pricing' | 'professionalServices';

export const ADMIN_SEARCH_DEBOUNCE_MS = 500;
const ALL_ADMIN_DASHBOARD_TABS: AdminDashboardTab[] = [
    'companies',
    'users',
    'sows',
    'pricing',
    'professionalServices',
];

export function getVisibleAdminDashboardTabs(): AdminDashboardTab[] {
    return ALL_ADMIN_DASHBOARD_TABS;
}

export function getDefaultAdminDashboardTab(): AdminDashboardTab {
    return ALL_ADMIN_DASHBOARD_TABS[0];
}

export function parseAdminTabParam(value: string | null): AdminDashboardTab | null {
    if (!value) {
        return null;
    }

    return ALL_ADMIN_DASHBOARD_TABS.includes(value as AdminDashboardTab)
        ? (value as AdminDashboardTab)
        : null;
}

export function buildAdminDashboardSearchParams(
    tab: AdminDashboardTab,
    browseEnabledByTab: Partial<Record<AdminDashboardTab, boolean>>,
): URLSearchParams {
    const params = new URLSearchParams();
    params.set('tab', tab);
    if (browseEnabledByTab[tab]) {
        params.set('browse', '1');
    }
    return params;
}

export function getAdminSearchPlaceholder(tab: AdminDashboardTab): string {
    switch (tab) {
        case 'companies':
            return 'Search by company name or ID';
        case 'sows':
            return 'Search by SOW name or ID';
        case 'users':
            return 'Search by user ID, name, or email';
        case 'pricing':
            return 'Search by pricing config name';
        case 'professionalServices':
            return 'Search by professional service name or ID';
        default: {
            const exhaustiveCheck: never = tab;
            return exhaustiveCheck;
        }
    }
}

export function shouldFetchAdminTab(
    tab: AdminDashboardTab,
    query: string | undefined,
    browseEnabled: boolean,
): boolean {
    if (tab === 'companies' || tab === 'users') {
        return Boolean(query) || browseEnabled;
    }

    return true;
}

export function getAdminEmptyBrowseCopy(tab: AdminDashboardTab): string {
    switch (tab) {
        case 'companies':
            return 'Search for a company by name or ID, or browse all companies when you need the full list.';
        case 'users':
            return 'Search for a user by ID, name, or email, or browse all users when you need the full list.';
        case 'sows':
            return 'Search by SOW name or ID.';
        case 'pricing':
            return 'Search by pricing config name.';
        case 'professionalServices':
            return 'Search by professional service name or ID.';
        default: {
            const exhaustiveCheck: never = tab;
            return exhaustiveCheck;
        }
    }
}

export function getAdminNewActionLabel(tab: AdminDashboardTab): string | null {
    switch (tab) {
        case 'companies':
            return '+ New Company';
        case 'users':
            return '+ New User';
        case 'pricing':
            return '+ Add Pricing Config';
        case 'sows':
            return null;
        case 'professionalServices':
            return '+ Add Professional Service';
        default: {
            const exhaustiveCheck: never = tab;
            return exhaustiveCheck;
        }
    }
}

export function isImmediateSearchKey(key: string): boolean {
    return key === 'Enter';
}

/** Displays `pricing_config` created/updated timestamps in admin screens. */
export function formatPricingConfigDateTime(value: string | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date);
}

/** Prefix of the row id shown in selects; search still uses the full id string. */
export function pricingConfigIdSlug(id: string): string {
    return id.slice(0, 6);
}

/** Search/filters pricing config selects by display name or full row id only. */
export function pricingConfigSelectSearchText(config: PricingConfigSummary): string {
    return `${config.name} ${config.id}`;
}

function pricingConfigGroupKey(config: PricingConfigSummary): string {
    return config.pricingConfigGroupId || config.id;
}

function comparePricingConfigVersions(
    left: PricingConfigSummary,
    right: PricingConfigSummary,
): number {
    if (right.version !== left.version) return right.version - left.version;
    return Date.parse(right.createdAt ?? '') - Date.parse(left.createdAt ?? '');
}

/** Keeps only the highest version row for each pricing configuration group. */
export function pickLatestPricingConfigVersions(
    configs: readonly PricingConfigSummary[],
): PricingConfigSummary[] {
    const byGroup = new Map<string, PricingConfigSummary[]>();
    for (const config of configs) {
        const groupId = pricingConfigGroupKey(config);
        byGroup.set(groupId, [...(byGroup.get(groupId) ?? []), config]);
    }

    return Array.from(byGroup.values()).map((groupRows) => {
        const sortedRows = [...groupRows].sort(comparePricingConfigVersions);
        return sortedRows[0];
    });
}

export function filterActivePricingConfigs(
    configs: readonly PricingConfigSummary[],
): PricingConfigSummary[] {
    return configs.filter((config) => config.isActive);
}

/** True when `config` matches the current pricing JSON schema (unitType + pro + pulse). */
export function isPricingConfigPayload(config: unknown): config is PricingConfigPayload {
    if (!config || typeof config !== 'object' || Array.isArray(config)) return false;
    const record = config as Record<string, unknown>;
    return (
        (record.unitType === 'credit' || record.unitType === 'token') &&
        Boolean(record.pro) &&
        Boolean(record.pulse)
    );
}

/** Short label for admin tables; tolerates legacy configs without `unitType` / `activities`. */
export function formatPricingConfigActivitiesSummary(config: unknown): string {
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
        return '—';
    }
    const configRecord = config as Record<string, unknown>;
    const unitRaw = configRecord.unitType;
    const unitLabel = unitRaw === 'token' ? 'Token' : unitRaw === 'credit' ? 'Credit' : null;

    const rawPro = configRecord.pro;
    const pro =
        rawPro && typeof rawPro === 'object' && !Array.isArray(rawPro)
            ? (rawPro as Record<string, unknown>)
            : null;
    const rawPulse = configRecord.pulse;
    const pulse =
        rawPulse && typeof rawPulse === 'object' && !Array.isArray(rawPulse)
            ? (rawPulse as Record<string, unknown>)
            : null;
    const sampleSizes = Array.isArray(pro?.sampleSizes)
        ? pro.sampleSizes
              .map((entry) =>
                  entry && typeof entry === 'object' && !Array.isArray(entry)
                      ? Number((entry as Record<string, unknown>).n)
                      : NaN,
              )
              .filter(Number.isFinite)
        : [];
    const b2bCredits = pro ? Number(pro.b2bCredits) : NaN;
    const b2cCredits = pro ? Number(pro.b2cCredits) : NaN;
    const standardPulse = pulse ? Number(pulse.standardPulse) : NaN;

    if (
        !unitLabel ||
        !Number.isFinite(b2bCredits) ||
        !Number.isFinite(b2cCredits) ||
        !Number.isFinite(standardPulse) ||
        sampleSizes.length === 0
    ) {
        return '—';
    }

    return `${unitLabel}: B2B ${b2bCredits}, B2C ${b2cCredits}, Pulse ${standardPulse}, samples ${sampleSizes.join('/')}`;
}
